// @flow
import * as React from 'react';
import {
  Checkbox, Select, Option,
} from 'informed';
import { Label, PrimaryText } from '../../../Typography.jsx';
import ValidateText from '../../../ValidateText.jsx';
import NumericText from '../../../NumericText.jsx';

/*
  Vitals rules
  Include head circ up to 3 years (inclusive)
  hgb in 6 month, 9 month, each from 2 year
  bp starting from 3 years (inclusive)
  display length as height from 3 years (inclusive)
  HIV rapid test for 12 month, 18 month
*/

type Props = {
  vitals: Object,
  type: number,
}

class EditVitals extends React.Component<Props> {
  render() {
    const {
      weight, length, headCircumference, bp, bpSite, bpPosition, spo2, spo2Site,
      temperature, temperatureSite, hr, hrSite, rr, hgb, hgbIron, hivTest,
    } = this.props.vitals;

    return (
      <div>
        <PrimaryText>General</PrimaryText>
        <table>
          <tbody>
            <tr>
              <td><Label>Weight:</Label></td>
              <td width='80'><NumericText field='vitals.weight' initialValue={weight} size={8} /></td>
              <td>
                <Select field='weightUnit'>
                  <Option value='kg'>kg</Option>
                  <Option value='lb'>lb</Option>
                </Select>
              </td>
            </tr>
            <tr>
              <td><Label>{this.props.type > 7 ? 'Height:' : 'Length:'}</Label></td>
              <td><NumericText field='vitals.length' initialValue={length} size={8} /></td>
              <td>
                <Select field='lengthUnit'>
                  <Option value='cm'>cm</Option>
                  <Option value='in'>in</Option>
                </Select>
              </td>
            </tr>
            {this.props.type < 9
              && <tr>
                <td width='175'><Label>Head Circumference:</Label></td>
                <td><NumericText field='vitals.headCircumference' initialValue={headCircumference} size={8} /></td>
                <td>
                  <Select field='headCircumferenceUnit'>
                    <Option value='cm'>cm</Option>
                    <Option value='in'>in</Option>
                  </Select>
                </td>
              </tr>
            }
            <tr>
              <td><Label>HGB/HCT:</Label></td>
              <td><NumericText field='vitals.hgb' initialValue={hgb} size={8} /></td>
              <td>g/dL</td>
            </tr>
            <tr>
              <td><Label>For Hb Recheck:</Label></td>
              <td><Checkbox field='vitals.hgbIron' initialValue={hgbIron} /></td>
            </tr>
            {(this.props.type === 5 || this.props.type === 6) // 12m and 18m WCCs only
              && <tr>
                <td><Label>Rapid HIV Test:</Label></td>
                <td>
                  <Select field='vitals.hivTest' initialValue={hivTest}>
                    <Option value={''}>--</Option>
                    <Option value={'Positive'}>Positive</Option>
                    <Option value={'Negative'}>Negative</Option>
                    <Option value={'Unknown'}>Unknown</Option>
                  </Select>
                </td>
              </tr>
            }
          </tbody>
        </table>
        <br />
        <PrimaryText>Vitals</PrimaryText>
        <table>
          <tbody>
            <tr>
              <td><Label>Temperature:</Label></td>
              <td><NumericText field='vitals.temperature' initialValue={temperature} size={8} /></td>
              <td>
                <Select field='temperatureUnit'>
                  <Option value='f'>°F</Option>
                  <Option value='c'>°C</Option>
                </Select>
              </td>
            </tr>
            <tr>
              <td>Site:</td>
              <td>
                <Select field='vitals.temperatureSite' initialValue={temperatureSite}>
                  <Option>--</Option>
                  <Option value='Oral'>Oral</Option>
                  <Option value='Tympanic'>Tympanic</Option>
                  <Option value='Axillary'>Axillary</Option>
                  <Option value='Rectal'>Rectal</Option>
                  <Option value='Other (see notes)'>Other (see notes)</Option>
                </Select>
              </td>
            </tr>
            <tr>
              <td><Label>Respiration Rate: </Label></td>
              <td><NumericText field='vitals.rr' initialValue={rr} size={8} /></td>
              <td>breaths per min</td>
            </tr>
            <tr>
              <td><Label>Heart Rate:</Label></td>
              <td><NumericText field='vitals.hr' initialValue={hr} size={8} /></td>
              <td>bpm</td>
            </tr>
            <tr>
              <td>Site:</td>
              <td>
                <Select field='vitals.hrSite' initialValue={hrSite}>
                  <Option>--</Option>
                  <Option value='Apical'>Apical</Option>
                  <Option value='Left Radial'>Left Radial</Option>
                  <Option value='Right Radial'>Right Radial</Option>
                  <Option value='Other (see notes)'>Other (see notes)</Option>
                </Select>
              </td>
            </tr>
            <tr>
              <td><Label>BP:</Label></td>
              <td>
                <ValidateText
                  placeholder='#/#'
                  field='vitals.bp'
                  initialValue={bp}
                  size={8}
                  regex={/^(\d+\/\d+)?$/}
                  errorText={'Please enter valid BP, (e.g. 120/80)'}
                />
              </td>
            </tr>
            <tr>
              <td>Site:</td>
              <td>
                <Select field='vitals.bpSite' initialValue={bpSite}>
                  <Option>--</Option>
                  <Option value='Left Brachial'>Left Brachial</Option>
                  <Option value='Right Brachial'>Right Brachial</Option>
                  <Option value='Left Leg'>Left Leg</Option>
                  <Option value='Right Leg'>Right Leg</Option>
                  <Option value='Other (see notes)'>Other (see notes)</Option>
                </Select>
              </td>
            </tr>
            <tr>
              <td>Position:</td>
              <td>
                <Select field='vitals.bpPosition' initialValue={bpPosition}>
                  <Option>--</Option>
                  <Option value='Sitting'>Sitting</Option>
                  <Option value='Standing'>Standing</Option>
                  <Option value='Lying'>Lying</Option>
                </Select>
              </td>
            </tr>
            <tr>
              <td><Label>SpO2:</Label></td>
              <td><NumericText field='vitals.spo2' initialValue={spo2} size={8} /></td>
              <td>%</td>
            </tr>
            <tr>
              <td>Site:</td>
              <td>
                <Select field='vitals.spo2Site' initialValue={spo2Site}>
                  <Option>--</Option>
                  <Option value='Left Finger'>Left Finger</Option>
                  <Option value='Right Finger'>Right Finger</Option>
                  <Option value='Left Toe'>Left Toe</Option>
                  <Option value='Right Toe'>Right Toe</Option>
                  <Option value='Other (see notes)'>Other (see notes)</Option>
                </Select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

export default EditVitals;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/wellChildCheck/edit/EditVitals.jsx